import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);
// Legacy localStorage key — cleared on logout so migrated users don't
// carry a stale bearer for 8 h.
const LEGACY_TOKEN_KEY = "to_token";
const IS_DEV = process.env.NODE_ENV !== "production";

/** localStorage may throw in Safari private mode, disabled-storage browsers,
 *  or when quota is exhausted. Every one of those failures is genuinely
 *  ignorable here — the legacy key is a migration shim, not a source of
 *  truth — but we still surface the error to the console in dev so a real
 *  regression doesn't hide behind a swallow. */
const safeClearLegacyToken = () => {
  try { localStorage.removeItem(LEGACY_TOKEN_KEY); }
  catch (e) { if (IS_DEV) console.warn("[auth] localStorage unavailable:", e); }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking | false = unauth | object = auth
  const [loading, setLoading] = useState(true);

  // Bootstrap: always call /auth/me — the HttpOnly cookie (or a legacy
  // localStorage bearer forwarded by the axios interceptor) will drive auth.
  useEffect(() => {
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        safeClearLegacyToken();
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    // Backend now sets the HttpOnly `to_token` cookie. We no longer
    // stash the raw JWT in localStorage — cookies are the source of truth.
    safeClearLegacyToken();
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort: a network error must not block local state cleanup.
    try { await api.post("/auth/logout"); }
    catch (e) { if (IS_DEV) console.warn("[auth] server logout failed (clearing locally anyway):", e); }
    safeClearLegacyToken();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
